import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { hhmm, formatReadingValue } from '../../utils/checkSets';

// "Basic vitals ✓ 9:52 AM · Priya Raman" — one chip per LIVE reading of a visit,
// on the desk's appointment row and in its detail. The tick is a lucide icon.
export function ReadingChip({ reading, testId }) {
    return (
        <span
            data-testid={testId || `reading-chip-${reading.readingId}`}
            title={reading.recordedByName || undefined}
            className="inline-flex max-w-full items-center gap-1 rounded-full border border-[#4ABDBD]/30 bg-[#4ABDBD]/10 px-2 py-0.5 text-[11px] font-semibold text-[#2D8A8A]"
        >
            <span className="truncate">{reading.setName || reading.setSnapshot?.name}</span>
            <Check className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="shrink-0">{hhmm(reading.recordedAt)}</span>
            {reading.recordedByName && <span className="truncate font-medium">· {reading.recordedByName}</span>}
        </span>
    );
}

export default function ReadingChips({ readings, className = '', testId }) {
    if (!readings || readings.length === 0) return null;
    return (
        <span data-testid={testId} className={`flex flex-wrap gap-1 ${className}`}>
            {readings.map((r) => <ReadingChip key={r.readingId} reading={r} />)}
        </span>
    );
}

// A reading's values as label / value lines — always from the reading's own
// snapshot (`items`), never the live set: editing a set never relabels a
// reading already taken.
export function ReadingItems({ items, testId, dense = false }) {
    const { t } = useTranslation();
    if (!items || items.length === 0) return null;
    return (
        <dl data-testid={testId} className={`grid grid-cols-1 gap-x-4 ${dense ? 'gap-y-0.5 sm:grid-cols-2' : 'gap-y-1 sm:grid-cols-2'}`}>
            {items.map((item) => (
                <div key={item.key} className="flex min-w-0 items-baseline gap-2 text-sm">
                    <dt className="shrink-0 text-gray-500">{item.label}</dt>
                    <dd className="min-w-0 break-words font-semibold text-gray-800">{formatReadingValue(item, t)}</dd>
                </div>
            ))}
        </dl>
    );
}
